<template>
  <div class="login-form">
    <h1 class="login-form__title">欢迎登录有言服务平台</h1>

    <div class="login-form__main">
      <IconWechat class="login-form__main-icon" />
      微信扫码登录
    </div>

    <div class="login-form__sub">关注「魔珐有言」公众号，完成账号绑定</div>
    <div class="login-form__qrcode-wrap">
      <div class="login-form__qrcode-container">
        <ClientOnly>
          <XQrcode
            :class="{
              'login-form__qrcode': true,
              'login-form__qrcode--blur':
                !isProtocolConfirm ||
                !qrcodeTicketInfo?.url ||
                isFetchingQrcode ||
                isQrcodeTimeOuted,
            }"
            :url="qrcodeTicketInfo?.url || 'https://www.youyan3d.com'"
            :size="180"
          ></XQrcode>
        </ClientOnly>
        <div
          v-if="isFetchingQrcode || isQrcodeTimeOuted || isScanned || !isProtocolConfirm"
          class="login-form__mask"
        >
          <div v-if="!isProtocolConfirm" class="login-form__qrcode-tip">请先确认协议</div>
          <Loading v-if="isFetchingQrcode" :overlay="false" color="var(--x-white-1)" />
          <div v-if="isScanned" class="login-form__qrcode-tip">
            <Text
              variant="14"
              color="p6"
              class="login-form__qrcode-tip-refresh"
              @click="refreshQrcode"
            >
              二维码已使用，点此刷新
            </Text>
          </div>
          <div
            v-if="isQrcodeTimeOuted"
            class="login-form__qrcode-tip login-form__qrcode-tip--timeout"
          >
            <Text
              variant="14"
              color="p6"
              class="login-form__qrcode-tip-refresh"
              @click="refreshQrcode"
            >
              二维码已过期，点此刷新
            </Text>
          </div>
        </div>
      </div>
    </div>

    <div
      class="login-form__refresh-panel"
      :class="{
        'login-form__refresh-hidden': !isProtocolConfirm,
      }"
    >
      <Text variant="12" color="p6" class="login-form__qrcode-tip-refresh" @click="refreshQrcode">
        若显示异常，可点此刷新
      </Text>
    </div>

    <Button
      variant="border-back"
      size="large"
      @click="emits('switch')"
      class="login-form__switch-mode"
    >
      其他登录方式
    </Button>

    <ProtocolCheckbox
      :use-checkbox="true"
      v-model="isProtocolConfirm"
      user-agreement-url="https://frontend-protocol.xmov.ai/youyan-v3/user"
      privacy-policy-url="https://frontend-protocol.xmov.ai/youyan-v3/privacy"
      user-agreement-text="《用户协议》"
      privacy-policy-text="《隐私政策》"
      prefix-text="我已阅读并同意"
      middle-text="和"
      @change="handleProtocolChange"
    />
    <BindPhone
      v-if="qrcodeTicketInfo?.ticket_id"
      ref="phoneRef"
      :ticketId="qrcodeTicketInfo?.ticket_id"
      @ok="onBindPhoneSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import IconWechat from '@rendora/design/assets/color/wechat.svg?component';
import { Button } from '@rendora/design/components/button';
import { Loading } from '@rendora/design/components/loading';
import { Text } from '@rendora/design/components/text';

import {
  getWechatOfficialAccountScanStatus,
  getWechatOfficialAccountTicket,
  IScanStatus,
  type IWechatOfficialAccountTicket,
} from '~/api/module/open-login';
import { useProtocolConfirm } from '../../../shared/lib/useProtocolConfirm';
import BindPhone from '../../bind-phone/index.vue';
import { trackData } from '../lib/trackData';
import ProtocolCheckbox from './ProtocolCheckbox.vue';

import type { Account } from '@rendora/common/entities/user';
import { useUserStore } from '@rendora/common/store';
import { getUserInfo } from '~/api';
import type { LoginInfo } from '~/api/interface';
import XQrcode from '~/components/xQrcode/index.vue';
import { SESSION_SOURCE } from '~/constants/config';
import { sessionRemoveItem } from '~/utils/localStorage';
import { buriedLogin, TRACK_EVENT, trackEvent, USER_TYPE } from '~/utils/track';

const isQrcodeTimeOuted = ref(false);
const isFetchingQrcode = ref(false);
const isProtocolConfirm = ref();
const qrcodeTicketInfo = ref<IWechatOfficialAccountTicket>();
const { show: showProtocolConfirm } = useProtocolConfirm();

// 倒计时
const countdown = ref();
let countdownTimer: number;
function startQrcodeTimeOut(time: number) {
  countdown.value = time;
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  countdownTimer = window.setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      isQrcodeTimeOuted.value = true;
      // 埋点：微信二维码过期
      trackEvent(TRACK_EVENT.LOGIN_WECHAT_QRCODE_EXPIRED);
    }
  }, 1000);
}

const phoneRef = ref<typeof BindPhone>();

// 状态轮询
let statusLoopTimer: number;
const emits = defineEmits(['success', 'accountSelect', 'switch']);
const { setAccountList, setSession, setUserInfo } = useChangeUser();
const isScanned = ref<boolean>(false);
const hasScannedTracked = ref<boolean>(false); // 用于跟踪是否已经记录过扫描埋点
async function startLoginStatusLoop(ticketId: string) {
  const res = await getWechatOfficialAccountScanStatus({ ticket_id: ticketId });
  if (res.data) {
    if (res.data.status === IScanStatus.PENDING) {
      // 埋点：微信二维码扫描（只在第一次进入 PENDING 状态时记录）
      if (!hasScannedTracked.value) {
        hasScannedTracked.value = true;
        trackEvent(TRACK_EVENT.LOGIN_WECHAT_QRCODE_SCAN);
      }
      if (statusLoopTimer) {
        window.clearTimeout(statusLoopTimer);
      }
      statusLoopTimer = window.setTimeout(() => {
        startLoginStatusLoop(ticketId);
      }, 5000);
    }
    if (res.data.action === 'register') {
      // 埋点：微信登录需要绑定手机号
      trackEvent(TRACK_EVENT.LOGIN_WECHAT_BIND_PHONE);
      return phoneRef.value?.toggle();
    }
    if (res.data.status === IScanStatus.SUCCESS) {
      onBindPhoneSuccess(res.data.login_data);
    }
  }
}

async function refreshQrcode() {
  try {
    if (!isProtocolConfirm.value) {
      return showProtocolConfirm(() => {
        isProtocolConfirm.value = true;
        refreshQrcode();
      });
    }
    isFetchingQrcode.value = true;
    isQrcodeTimeOuted.value = false;
    isScanned.value = false;
    hasScannedTracked.value = false; // 重置扫描埋点标记

    // 埋点：微信二维码刷新（如果不是首次生成）
    if (qrcodeTicketInfo.value) {
      trackEvent(TRACK_EVENT.LOGIN_WECHAT_QRCODE_REFRESH);
    }

    const res = await getWechatOfficialAccountTicket();

    if (res.data) {
      qrcodeTicketInfo.value = res.data;
      startQrcodeTimeOut(res.data.expired_in); // 14 分钟
      startLoginStatusLoop(res.data.ticket_id);
      // 埋点：微信二维码生成
      trackEvent(TRACK_EVENT.LOGIN_WECHAT_QRCODE_GENERATE, {
        ticket_id: res.data.ticket_id,
        expired_in: res.data.expired_in,
      });
    }
  } catch (err: any) {
    console.error(err);
    // 埋点：微信登录失败
    trackEvent(TRACK_EVENT.LOGIN_WECHAT_FAIL, {
      error_code: err?.code || 'unknown',
      error_msg: err?.msg || err?.message || '二维码生成失败',
      step: 'generate_qrcode',
    });
  }
  isFetchingQrcode.value = false;
}

function handleProtocolChange(val: boolean) {
  if (val && !qrcodeTicketInfo.value) {
    refreshQrcode();
  }
}

async function onBindPhoneSuccess(login_data: LoginInfo[]) {
  isScanned.value = true;

  const userStore = useUserStore();

  userStore.setAccountList(login_data as unknown as Account[]);

  // 用户账号列表
  setAccountList(login_data);

  sessionRemoveItem(SESSION_SOURCE);
  if (login_data.length > 1) {
    emits('accountSelect', login_data);
  } else {
    // 用户token
    await setSession(login_data[0]);
    buriedLogin(login_data[0]?.user_id);
    trackData(
      'wechat',
      { account_name: login_data[0].account_name },
      {
        NewUser: login_data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
      },
    );
    // 埋点：微信登录成功
    trackEvent(TRACK_EVENT.LOGIN_WECHAT_SUCCESS, {
      account_name: login_data[0].account_name,
      NewUser: login_data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
    });
    const result = await getUserInfo();
    setUserInfo(result.data!);
    emits('success', result.data);
  }
}

onBeforeUnmount(() => {
  window.clearInterval(statusLoopTimer);
  window.clearInterval(countdownTimer);
});
</script>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.login-form__title {
  font-size: var(--x-text-24-size);
  font-weight: var(--x-weight-medium);
  line-height: var(--x-text-24-line-height);
  text-align: center;
  color: var(--x-text-1);
  margin-bottom: 48px;

  @include mobile {
    margin-bottom: responsive-size(48px);
  }
}

.login-form__qrcode-tip-refresh {
  cursor: pointer;
}

.login-form__main {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: var(--x-weight-medium);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  gap: 8px;
  margin-bottom: 12px;

  @include mobile {
    gap: responsive-size(8px);
    margin-bottom: responsive-size(12px);
  }
}

.login-form__sub {
  text-align: center;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-5);
  margin-bottom: 12px;

  @include mobile {
    margin-bottom: responsive-size(12px);
  }
}

.login-form__qrcode {
  width: 180px;
}

.login-form__qrcode--blur {
  filter: blur(3px);
}

.login-form__qrcode-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: 0 auto;
  width: 200px;
  height: 200px;
  border: 1px solid var(--x-back-5);
  border-radius: 8px;

  @include mobile {
    width: responsive-size(200px);
    height: responsive-size(200px);
  }
}

.login-form__qrcode-container {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  height: 180px;
  width: 180px;
  background-color: var(--x-back-6);

  @include mobile {
    height: responsive-size(180px);
    width: responsive-size(180px);
  }
}

.login-form__mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--x-black-6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-form__qrcode-tip {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: var(--x-text-1);
  width: 116px;
  gap: 8px;
  border-radius: 8px;
  padding: 11px 0;
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  background: var(--x-back-1);
  box-shadow: var(--x-shadow);
  border: 1px solid var(--x-back-4);

  @include mobile {
    width: responsive-size(116px);
    gap: responsive-size(8px);
    padding: responsive-size(11px) 0;
  }
}

.login-form__tip {
  margin-top: 12px;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-1);

  @include mobile {
    margin-top: responsive-size(12px);
  }
}

.login-form__tip--colored {
  color: var(--purple-6);
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
}

.login-form__switch-mode {
  margin: 48px auto 0;
  width: 100%;

  @include mobile {
    margin: responsive-size(48px) auto 0;
  }
}

.login-form__refresh-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin: 12px auto 0;
  text-align: center;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);

  @include mobile {
    gap: responsive-size(4px);
    margin: responsive-size(12px) auto 0;
  }
}

.login-form__refresh-hidden {
  visibility: hidden;
}
</style>
